import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom'; 
import api from '../../api'; 
import { toast } from "react-toastify";

const ProductDetails = () => {
  const { id } = useParams(); 
  const history = useHistory(); 

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState("");
  
  const [quantity, setQuantity] = useState(1);
  const [selectedPack, setSelectedPack] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  // --- REVIEW STATE ---
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({
      name: '',
      email: '',
      rating: 5,
      comment: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  // --- NEW STATE FOR IMAGE PREVIEW MODAL ---
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // --- 1. FETCH DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (!id) return;

        const storedUserId = localStorage.getItem("userId");
        const safeUserId = storedUserId ? storedUserId : "0";

        // A. Fetch Current Product
        const productRes = await api.get(`/products/productId?id=${id}&userId=${safeUserId}`);
        const currentProduct = productRes.data;
        
        if (currentProduct) {
            setProduct(currentProduct);
            
            // Set Active Image
            if(currentProduct.productPhotos && currentProduct.productPhotos.length > 0) {
               const firstPhoto = currentProduct.productPhotos[0];
               const photoName = firstPhoto.photoName ? firstPhoto.photoName : firstPhoto;
               setActiveImg(photoName);
            }

            // --- B. FETCH RELATED PRODUCTS ---
            try {
                const allProductsRes = await api.get('/products/hiddenFalse'); 
                const allProducts = allProductsRes.data || [];

                let filtered = allProducts.filter(item => {
                    if (String(item.productId) === String(currentProduct.productId)) return false;
                    if (currentProduct.category && item.category === currentProduct.category) return true;
                    if (currentProduct.categoryId && item.categoryId === currentProduct.categoryId) return true;
                    return false;
                });

                if (filtered.length === 0) {
                    filtered = allProducts.filter(item => String(item.productId) !== String(currentProduct.productId));
                }
                setRelatedProducts(filtered.slice(0, 4)); 
            } catch (err) {
                console.log("Could not fetch related products:", err);
            }

        } else {
            setProduct(null); 
        }

        // --- C. FETCH REVIEWS (Real Backend) ---
        try {
            const reviewsRes = await api.get(`/product-ratings/product/${id}`);
            setReviews(reviewsRes.data || []);
        } catch (err) {
            console.log("Error fetching reviews", err);
        }

      } catch (error) {
        console.error("Error fetching details:", error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);


  // --- 2. ACTIONS ---
  const addToCart = async () => {
    const userId = localStorage.getItem("userId");
    
    if (!userId || userId === "0" || userId === "guest") {
      toast.info("Please Log in to add items to your cart!");
      history.push('/login'); 
      return;
    }

    try {
      setAddingToCart(true);
      await api.post('/usercart/add-product', null, {
        params: {
          userId: userId,
          productId: id,
          orderQuantity: quantity * selectedPack 
        }
      });
      toast.success("Item added to cart successfully!");
    } catch (error) {
      console.error("Add to cart failed:", error);
      toast.error("Failed to add item.");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleReviewSubmit = async (e) => {
      e.preventDefault();
      if(!reviewForm.name || !reviewForm.comment) {
          toast.error("Please fill in required fields.");
          return;
      }
      try {
          setSubmittingReview(true);
          
          const payload = {
              productId: id,
              userName: reviewForm.name,
              email: reviewForm.email,
              message: reviewForm.comment,
              ratings: String(reviewForm.rating)
          };

          const response = await api.post('/product-ratings', payload);
          setReviews([response.data, ...reviews]);
          setReviewForm({ name: '', email: '', rating: 5, comment: '' });
          toast.success("Review posted successfully!");
      } catch (error) {
          console.error("Review Error:", error);
          toast.error("Failed to post review.");
      } finally {
          setSubmittingReview(false);
      }
  };

  const renderBullets = (text) => {
    if (!text) return null;
    const lines = text.split('\n').filter(line => line.trim() !== '');
    if (lines.length === 0) return null;

    return (
        <ul className="mt-2 ps-3" style={{ listStyleType: 'disc' }}>
            {lines.map((line, index) => (
                <li key={index} className="text-muted mb-2" style={{ lineHeight: '1.5' }}>
                    {line}
                </li>
            ))}
        </ul>
    );
  };

  // --- 3. RENDER UI ---
  if (loading) return <div className="text-center py-5"><h3>Loading details...</h3></div>;
  if (!product) return <div className="text-center py-5"><h3>Product not found.</h3></div>;

  const price = Number(product.productPrice || product.price || 0);
  const mrp = Number(product.productMRP || product.mrp || product.MRP || 0);
  
  // Calculate totals based on Pack Size
  const currentPrice = price * selectedPack; 
  const currentMRP = mrp * selectedPack;

  const name = product.productName || product.name;
  
  const shortDesc = product.shortDesc || ""; 
  const detailedDesc = product.prdDesc || product.productDescription || ""; 
  
  const images = (product.productPhotos && product.productPhotos.length > 0) 
                  ? product.productPhotos.map(p => p.photoName || p) 
                  : ["/assets/images/Rectangle 129.png"];

  const avgRating = reviews.length > 0 
      ? (reviews.reduce((acc, curr) => acc + parseFloat(curr.ratings || 0), 0) / reviews.length).toFixed(1)
      : 0;

  // --- FIXED: USE productQuantity for Pack Size ---
  const rawMaxPack = parseInt(product.productQuantity);
  const maxPackSize = (!isNaN(rawMaxPack) && rawMaxPack > 0) ? rawMaxPack : 3;
  const packOptions = Array.from({ length: maxPackSize }, (_, i) => i + 1);

  return (
    <div className="container py-5" style={{ minHeight: '80vh' }}>
      
      {/* === TOP SECTION: IMAGES & BASIC INFO === */}
      <div className="row mb-5 bg-white p-4 rounded shadow-sm">
        
        {/* Left: Images */}
        <div className="col-md-5 mb-4 mb-md-0">
          
          {/* --- CHANGE 1: Full Size Image Container with Zoom Click --- */}
          <div 
             className="mb-3 text-center border rounded position-relative" 
             style={{ 
                 cursor: 'zoom-in', 
                 backgroundColor: '#fff', 
                 overflow: 'hidden' 
             }}
             onClick={() => setShowModal(true)}
             title="Click to zoom"
          >
             <img 
               src={activeImg || images[0]} 
               alt="Main" 
               className="img-fluid" 
               style={{ 
                   width: '100%', 
                   height: 'auto', 
                   maxHeight: '600px', // Increased height limit significantly
                   objectFit: 'contain' 
               }} 
               onError={(e) => {e.target.onerror = null; e.target.src="https://via.placeholder.com/600?text=No+Image"}}
             />
             {/* Optional: Icon to indicate zoom capability */}
             <div className="position-absolute bottom-0 end-0 p-2 text-muted opacity-50">
                 <i className="fa fa-search-plus"></i>
             </div>
          </div>

          <div className="d-flex gap-2 justify-content-center overflow-auto">
            {images.map((img, idx) => (
              <img 
                key={idx} 
                src={img} 
                alt="thumb" 
                onClick={() => setActiveImg(img)}
                style={{ 
                  width: '80px', height: '80px', objectFit: 'cover', cursor: 'pointer', // Slightly larger thumbnails
                  border: activeImg === img ? '2px solid #FFC35B' : '1px solid #ddd' 
                }}
                className="rounded"
              />
            ))}
          </div>
        </div>

        {/* RIGHT: Basic Info & Pointers */}
        <div className="col-md-7 ps-md-5">
          
          <h1 className="fw-bold mb-2 display-6 text-dark">{name}</h1>
          
          <div className="d-flex align-items-center mb-3">
            <span className="text-warning fs-5 me-2">
                {'★'.repeat(Math.round(avgRating) || 5)}{'☆'.repeat(5 - (Math.round(avgRating) || 5))}
            </span>
            <span className="text-muted small">({reviews.length} Customer Reviews)</span>
          </div>

          {/* PRICE */}
          <div className="border-bottom pb-3 mb-4">
            <div className="d-flex align-items-center gap-3">
                {/* MRP DISPLAY */}
                {currentMRP > currentPrice && (
                    <h4 className="text-muted text-decoration-line-through mb-0">
                        ₹{currentMRP.toFixed(2)}
                    </h4>
                )}
                <h2 className="fw-bold text-dark mb-0">₹{currentPrice.toFixed(2)}</h2>
            </div>
            <span className="text-success small fw-bold">In Stock</span>
            {currentMRP > currentPrice && (
                <span className="badge bg-danger ms-2">
                    {Math.round(((currentMRP - currentPrice) / currentMRP) * 100)}% OFF
                </span>
            )}
          </div>
          
          {/* BULLET POINTS */}
          {shortDesc && (
            <div className="mb-4 p-3 bg-light rounded">
                <h6 className="fw-bold text-dark mb-2">About this item</h6>
                {renderBullets(shortDesc)}
            </div>
          )}

          {/* DYNAMIC PACK SELECTION */}
          <div className="mb-4">
            <label className="fw-bold mb-2 d-block text-secondary small text-uppercase">Pack Size</label>
            <div className="d-flex gap-2 flex-wrap">
              {packOptions.map(num => (
                <button 
                  key={num} 
                  className={`btn btn-sm px-3 py-2 ${selectedPack === num ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => setSelectedPack(num)}
                  style={{ borderRadius: '6px' }}
                >
                  Pack of {num}
                </button>
              ))}
            </div>
          </div>

          {/* Add to Cart */}
          <div className="d-flex align-items-center gap-3">
            <div className="input-group" style={{ width: '130px' }}>
              <button className="btn btn-outline-secondary" onClick={() => setQuantity(q => q > 1 ? q - 1 : 1)}>-</button>
              <input type="text" className="form-control text-center bg-white" value={quantity} readOnly />
              <button className="btn btn-outline-secondary" onClick={() => setQuantity(q => q + 1)}>+</button>
            </div>
            
            <button 
              className="btn btn-warning px-5 py-2 fw-bold rounded-pill text-white shadow-sm"
              onClick={addToCart}
              disabled={addingToCart}
            >
              {addingToCart ? "Adding..." : "ADD TO CART"}
            </button>
          </div>

        </div>
      </div>

      {/* === DETAILED DESCRIPTION === */}
      {detailedDesc && (
          <div className="row bg-white p-5 rounded shadow-sm mb-5">
            <div className="col-12">
                <h4 className="fw-bold border-bottom pb-3 mb-4">Product Description</h4>
                <div className="text-secondary" style={{ lineHeight: '1.8', whiteSpace: 'pre-line', fontSize: '1.05rem' }}>
                    {detailedDesc}
                </div>
            </div>
          </div>
      )}

      {/* === REVIEWS SECTION === */}
      <div className="row bg-white p-4 rounded shadow-sm mb-5">
          <div className="col-12 mb-4">
              <h3 className="fw-bold border-bottom pb-2">Customer Reviews</h3>
          </div>
          
          {/* LEFT: Review List */}
          <div className="col-md-7 pe-md-5">
              {reviews.length === 0 ? (
                  <div className="alert alert-light text-center p-5">
                    <h5>No reviews yet</h5>
                    <p className="text-muted">Be the first to review this product!</p>
                  </div>
              ) : (
                  reviews.map((rev, index) => (
                      <div key={index} className="mb-4 pb-3 border-bottom">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                             <h6 className="fw-bold mb-0">{rev.userName || rev.customerName || "Anonymous"}</h6>
                             <small className="text-muted">{rev.date ? new Date(rev.date).toLocaleDateString() : ""}</small>
                          </div>
                          <div className="text-warning small mb-2">{'★'.repeat(Number(rev.ratings) || 5)}</div>
                          <p className="text-secondary">{rev.message || rev.comment}</p>
                      </div>
                  ))
              )}
          </div>
          
          {/* RIGHT: Write a Review Form */}
          <div className="col-md-5 mt-4 mt-md-0">
              <div className="p-4 rounded bg-light border sticky-top" style={{ top: '20px' }}>
                  <h5 className="fw-bold mb-3">Write a Review</h5>
                  <form onSubmit={handleReviewSubmit}>
                      <div className="mb-3">
                          <label className="small fw-bold d-block mb-1">Your Rating</label>
                          <div className="text-warning fs-3" style={{cursor:'pointer'}}>
                              {[1,2,3,4,5].map(s => (
                                  <span key={s} onClick={()=>setReviewForm({...reviewForm, rating:s})} className="me-1">
                                      {s <= reviewForm.rating ? '★' : '☆'}
                                  </span>
                              ))}
                          </div>
                      </div>
                      
                      <div className="mb-3">
                          <label className="small fw-bold mb-1">Your Name</label>
                          <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Enter your name" 
                              value={reviewForm.name} 
                              onChange={e=>setReviewForm({...reviewForm, name:e.target.value})} 
                              required 
                          />
                      </div>

                      <div className="mb-3">
                          <label className="small fw-bold mb-1">Your Email</label>
                          <input 
                              type="email" 
                              className="form-control" 
                              placeholder="Enter your email" 
                              value={reviewForm.email} 
                              onChange={e=>setReviewForm({...reviewForm, email:e.target.value})} 
                              required 
                          />
                      </div>

                      <div className="mb-3">
                          <label className="small fw-bold mb-1">Your Review</label>
                          <textarea 
                              className="form-control" 
                              rows="4" 
                              placeholder="Share your thoughts..." 
                              value={reviewForm.comment} 
                              onChange={e=>setReviewForm({...reviewForm, comment:e.target.value})} 
                              required
                          ></textarea>
                      </div>

                      <button type="submit" className="btn btn-dark w-100 rounded-pill fw-bold" disabled={submittingReview}>
                          {submittingReview ? "Posting..." : "Submit Review"}
                      </button>
                  </form>
              </div>
          </div>
      </div>

      {/* === RELATED PRODUCTS === */}
      {relatedProducts.length > 0 && (
        <div className="mt-5 mb-5">
            <h3 className="fw-bold mb-4 border-bottom pb-2">Products related to this item</h3>
            <div className="row">
                {relatedProducts.map((item) => {
                    let imgUrl = "/assets/images/Rectangle 129.png";
                    if (item.productPhotos && item.productPhotos.length > 0) {
                        const first = item.productPhotos[0];
                        imgUrl = first.photoName ? first.photoName : first;
                    }

                    return (
                        <div key={item.productId} className="col-6 col-md-3 mb-4">
                            <Link to={`/product/${item.productId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <div className="card h-100 border-0 shadow-sm product-card hover-shadow" style={{ transition: 'transform 0.2s' }}>
                                    <div className="p-3 text-center" style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor:'#f9f9f9', borderTopLeftRadius:'10px', borderTopRightRadius:'10px' }}>
                                        <img 
                                            src={imgUrl} 
                                            alt={item.productName} 
                                            className="img-fluid" 
                                            style={{ maxHeight: '180px', objectFit: 'contain', mixBlendMode:'multiply' }}
                                            onError={(e) => {e.target.onerror = null; e.target.src="https://via.placeholder.com/200?text=No+Image"}}
                                        />
                                    </div>
                                    <div className="card-body text-center">
                                        <h6 className="card-title text-truncate fw-bold mb-1" title={item.productName}>
                                            {item.productName}
                                        </h6>
                                        <div className="text-warning small mb-2">★★★★★</div>
                                        <h5 className="fw-bold text-dark">₹{item.productPrice || item.price}</h5>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
      )}

      {/* --- CHANGE 2: Full Screen Image Preview Modal --- */}
      {showModal && (
        <div 
            style={{
                position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px'
            }}
            onClick={() => setShowModal(false)} // Close when clicking background
        >
            <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                <button 
                    onClick={() => setShowModal(false)}
                    className="btn btn-close btn-close-white position-absolute"
                    style={{ top: '-40px', right: 0, zIndex: 10 }}
                ></button>
                <img 
                    src={activeImg || images[0]} 
                    alt="Full Preview" 
                    style={{ 
                        maxWidth: '100%', 
                        maxHeight: '90vh', 
                        objectFit: 'contain',
                        borderRadius: '5px',
                        boxShadow: '0 0 20px rgba(0,0,0,0.5)'
                    }} 
                />
            </div>
        </div>
      )}

    </div>
  );
};

export default ProductDetails;